import { trim } from 'lodash';
import { Epic, StateObservable } from 'redux-observable';
import { catchError, Observable, of, throwError } from 'rxjs';
import { PayloadActionCreator } from 'typesafe-actions';
import { isAxiosError, UnauthorizedError } from 'lib/errors';
import logger from 'services/logger';
import { RootAction } from 'store/actions';
import { EpicDependencies } from 'store/epics';
import { State } from 'store/reducers';
import {
  SelectorIdentifierProps,
  SelectorSearchQueryProps,
  SelectorSortDirectionProps,
  SelectorSortFieldProps,
  SelectorTransactionStateProps,
} from 'types/store';

// Type of object being passed to the epic creator callback
interface EpicCreatorProps extends EpicDependencies {
  action$: Observable<RootAction>;
  state$: StateObservable<State>;
}

// Function accepted as a callback parameter to the epic creator
type EpicCreatorCallback = (props: EpicCreatorProps) => Observable<RootAction>;

/**
 * Create a new Redux Observable epic receiving action and state streams merged with all dependencies
 *
 * @param {EpicCreatorCallback} callback Epic callback
 */
export const createEpic$ =
  (callback: EpicCreatorCallback): Epic<RootAction, RootAction, State, EpicDependencies> =>
  (action$, state$, deps) =>
    callback({
      action$,
      state$,
      ...deps,
    });

/**
 * Custom RxJS operator allowing to catch Axios request errors and dispatching them as new actions
 *
 * @param {PayloadActionCreator} errorActionCreator Action creator to use when dispatching the error
 */
export const catchAxiosError =
  <TErrorAction extends RootAction['type']>(
    errorActionCreator: PayloadActionCreator<TErrorAction, Error>,
  ) =>
  (source$: Observable<RootAction>) =>
    source$.pipe(
      catchError(error => {
        // Intercept Axios errors and emit them using the specified action creator
        if (isAxiosError(error)) {
          logger.error('api', error.message);

          // Rethrow 401 Unauthorized response results as unauthorized exceptions
          if (error.response?.status === 401) {
            throw new UnauthorizedError(error.message);
          }

          return of(errorActionCreator(error));
        }

        // Rethrow all other errors
        return throwError(() => error);
      }),
    );

/**
 * Custom RxJS operator allowing to catch authentication errors and dispatching them as new actions
 *
 * @param {PayloadActionCreator} errorActionCreator Action creator to use when dispatching the error
 */
export const catchUnauthorizedError =
  <TErrorAction extends RootAction['type']>(
    errorActionCreator: PayloadActionCreator<TErrorAction, Error>,
  ) =>
  (source$: Observable<RootAction>) =>
    source$.pipe(
      catchError(error => {
        // Intercept unauthorized exceptions and emit them using the specified action creator
        if (error instanceof UnauthorizedError) {
          logger.error('auth', error.message);
          return of(errorActionCreator(error));
        }

        return throwError(() => error);
      }),
    );

/**
 * Selector identifier property getter
 *
 * @param {State} state Application state
 * @param {SelectorIdentifierProps} props Selector parameters
 */
export const getSelectorEntityId = (state: State, props: SelectorIdentifierProps) => props.id;

/**
 * Selector search query getter
 *
 * @param {State} state Application state
 * @param {SelectorSearchQueryProps} props Selector parameters
 */
export const getSelectorSearchQuery = (state: State, props: SelectorSearchQueryProps) =>
  trim(props.searchQuery);

/**
 * Selector sort direction getter
 *
 * @param {State} state Application state
 * @param {SelectorSortDirectionProps} props Selector parameters
 */
export const getSelectorSortDirection = (state: State, props: SelectorSortDirectionProps) =>
  props.sortDir;

/**
 * Selector sort property getter
 *
 * @param {State} state Application state
 * @param {SelectorSortFieldProps} props Selector parameters
 */
export const getSelectorSortKey = (state: State, props: SelectorSortFieldProps) => props.sortKey;

/**
 * Selector identifier property getter
 *
 * @param {State} state Application state
 * @param {SelectorTransactionStateProps} props Selector parameters
 */
export const getSelectorTransactionState = (state: State, props: SelectorTransactionStateProps) =>
  props.transactionState;
